import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Question } from '../../schemas/schemas';

interface QuestionBankModalProps {
  isOpen: boolean;
  onClose: () => void;
  questionBank: Question[];
  existingQuestions: Question[];
  onSelectQuestions: (questions: Question[]) => void;
  translations?: {
    title?: string;
    description?: string;
    search?: string;
    difficulty?: string;
    questionType?: string;
    tags?: string;
    cancel?: string;
    addSelectedQuestions?: string;
    [key: string]: string | undefined;
  };
}

export default function QuestionBankModal({ 
  isOpen, 
  onClose, 
  onSelectQuestions,
  questionBank,
  existingQuestions,
  translations = {}
}: QuestionBankModalProps) {
  // State for searching and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  
  // State for selected questions
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>(
    existingQuestions.map(q => q.question_id)
  );
  
  if (!isOpen) return null;
  
  // Filter questions based on search and filters
  const filteredQuestions = questionBank.filter(question => {
    // Filter by search term
    const matchesSearch = searchTerm === '' || 
      question.question_text.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by type
    const matchesType = selectedType === '' || 
      question.question_type === selectedType;
    
    return matchesSearch && matchesType;
  });
  
  // Toggle question selection
  const toggleQuestionSelection = (questionId: string) => {
    if (selectedQuestionIds.includes(questionId)) {
      setSelectedQuestionIds(selectedQuestionIds.filter(id => id !== questionId));
    } else {
      setSelectedQuestionIds([...selectedQuestionIds, questionId]);
    }
  };
  
  // Handle save action
  const handleSave = () => {
    const selectedQuestions = questionBank.filter(q => selectedQuestionIds.includes(q.question_id));
    onSelectQuestions(selectedQuestions);
    onClose();
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader>
          <CardTitle>{translations.title || 'Question Bank'}</CardTitle>
          <CardDescription>
            {translations.description || 'Search and select questions to add to your exercise'}
          </CardDescription>
        </CardHeader>
        
        <div className="flex border-b border-gray-200 dark:border-gray-800">
          {/* Search and filters sidebar */}
          <div className="w-1/3 border-r border-gray-200 dark:border-gray-800 p-4 overflow-y-auto">
            <div className="space-y-4">
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {translations.search || 'Search'}
                </label>
                <Input
                  id="search"
                  placeholder="Search questions..."
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {translations.questionType || 'Question Type'}
                </label>
                <select
                  id="type"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="boolean">True/False</option>
                  <option value="text">Text Input</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Question list */}
          <div className="w-2/3 flex flex-col">
            <CardContent className="flex-1 p-4 overflow-y-auto max-h-[60vh]">
              <div className="space-y-3">
                {filteredQuestions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {translations.noQuestions || 'No questions match your search criteria'}
                  </div>
                ) : (
                  filteredQuestions.map((question) => (
                    <div 
                      key={question.question_id} 
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        selectedQuestionIds.includes(question.question_id)
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                      onClick={() => toggleQuestionSelection(question.question_id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <Checkbox 
                              checked={selectedQuestionIds.includes(question.question_id)}
                              onCheckedChange={() => toggleQuestionSelection(question.question_id)}
                              className="mr-3"
                            />
                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
                              {question.question_type.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                            {question.question_text}
                          </p>
                          
                          {question.question_type === 'multiple_choice' && question.options && (
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Options:</span> {question.options.slice(0, 2).join(', ')}
                              {question.options.length > 2 && '...'}
                            </div>
                          )}
                          
                          {question.question_type === 'boolean' && (
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Answer:</span> {question.correct_answer}
                            </div>
                          )}
                          
                          {question.question_type === 'text' && (
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Expected:</span> {question.correct_answer}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
            
            <CardFooter className="border-t border-gray-200 dark:border-gray-800 flex justify-between items-center">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedQuestionIds.length} question(s) selected
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" onClick={onClose}>
                  {translations.cancel || 'Cancel'}
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={selectedQuestionIds.length === 0}
                >
                  {translations.addSelectedQuestions || 'Add Selected Questions'}
                </Button>
              </div>
            </CardFooter>
          </div>
        </div>
      </Card>
    </div>
  );
} 